import { Link } from "@tanstack/react-router";
import { ArrowRight, SendHorizontal, Sparkles } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { AtlasLauncher } from "@/components/markyt/AtlasLauncher";
import { RiskBadge } from "@/components/skills/RiskBadge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSkillCatalog } from "@/hooks/use-skills";
import { ATLAS_CHIPS, answer } from "@/lib/skills/atlas";
import type { SkillListItem } from "@/lib/skills/types";

interface AtlasMessage {
  id: number;
  by: "bot" | "user";
  text: string;
  skills?: SkillListItem[];
}

const GREETING: AtlasMessage = {
  id: 0,
  by: "bot",
  text: "Hi — I'm Markyt. Tell me what you're building or the problem you're hitting, and I'll point you at the right skills.",
};

export function AtlasFinder() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<AtlasMessage[]>([GREETING]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(1);

  // Only fetch the catalog once the user opens the finder.
  const { data: catalog, isError } = useSkillCatalog({ enabled: open });
  const ready = catalog !== undefined;

  // Pin the view to the latest message whenever a message lands or the sheet opens.
  const messageCount = messages.length;
  useEffect(() => {
    const body = bodyRef.current;
    if (open && messageCount > 0 && body) body.scrollTop = body.scrollHeight;
  }, [messageCount, open]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setMessages([GREETING]);
      setInput("");
      nextId.current = 1;
    }
  }

  function send(raw: string) {
    const text = raw.trim();
    if (!text || !catalog) return;
    const res = answer(text, catalog);
    setMessages((m) => [
      ...m,
      { id: nextId.current++, by: "user", text },
      { id: nextId.current++, by: "bot", text: res.reason, skills: res.skills },
    ]);
    setInput("");
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    send(input);
  }

  const placeholder = isError
    ? "Couldn't load skills"
    : ready
      ? "Describe your task…"
      : "Loading skills…";

  return (
    <>
      <AtlasLauncher expanded={open} onClick={() => setOpen(true)} />
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[420px]">
          <SheetHeader className="flex-row items-center gap-3 bg-navy px-5 py-4 text-white">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-white/10">
              <Sparkles className="size-5 text-cyan" />
            </span>
            <div className="min-w-0">
              <SheetTitle className="font-display text-base font-bold text-white">
                Ask Markyt
              </SheetTitle>
              <SheetDescription className="text-xs text-white/60">
                Rule-based · no AI guesswork
              </SheetDescription>
            </div>
          </SheetHeader>

          <div
            ref={bodyRef}
            className="flex flex-1 flex-col gap-3 overflow-y-auto bg-bg-2 px-4 py-4"
          >
            {messages.map((m) => (
              <div
                key={m.id}
                className={
                  m.by === "user" ? "max-w-[90%] self-end" : "w-full max-w-[90%] self-start"
                }
              >
                {m.text && (
                  <div
                    className={
                      m.by === "user"
                        ? "rounded-[12px] bg-navy px-3 py-2 text-sm text-white"
                        : "rounded-[12px] bg-white px-3 py-2 text-sm text-fg-1 shadow-sm"
                    }
                  >
                    <span className="sr-only">{m.by === "user" ? "You" : "Markyt"}: </span>
                    {m.text}
                  </div>
                )}
                {m.skills && m.skills.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2">
                    {m.skills.map((s) => (
                      <Link
                        key={s.slug}
                        to="/skill/$id"
                        params={{ id: s.slug }}
                        onClick={() => handleOpenChange(false)}
                        className="flex items-center gap-3 rounded-[10px] border border-border-1 bg-white px-3 py-2.5 transition-colors hover:bg-bg-2 focus-visible:outline-2 focus-visible:outline-fg-link"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-display text-sm font-bold text-navy">
                            {s.title}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <RiskBadge level={s.risk} />
                            <span className="text-xs font-medium text-fg-3">
                              ★ {s.rating.toFixed(1)}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="size-4 shrink-0 text-fg-3" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isError && (
              <div className="rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger">
                I couldn't load the skill catalog right now. Close and reopen to retry.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 border-t border-border-1 bg-white px-4 py-3">
            {ATLAS_CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                disabled={!ready}
                onClick={() => send(c)}
                className="rounded-full border border-border-1 bg-bg-2 px-3 py-1.5 text-xs font-medium text-fg-2 transition-colors hover:bg-bg-3 focus-visible:outline-2 focus-visible:outline-fg-link disabled:cursor-not-allowed disabled:opacity-50"
              >
                {c}
              </button>
            ))}
          </div>

          <form
            onSubmit={onSubmit}
            className="flex items-center gap-2 border-t border-border-1 bg-white px-4 py-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!ready}
              placeholder={placeholder}
              aria-label="Describe your task"
              className="h-10 flex-1 rounded-[9px] border border-border-1 bg-bg-2 px-3 text-sm text-fg-1 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <button
              type="submit"
              aria-label="Send"
              disabled={!ready}
              className="grid size-10 shrink-0 place-items-center rounded-[9px] bg-navy text-white transition-colors hover:bg-navy-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-link disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SendHorizontal className="size-4" />
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
